import React, { useState, useEffect, useRef } from 'react';
import useClickOutside from '../../../../../hooks/useClickOutside';
import { Box } from '@mui/system';
import { datadogLogs } from '@datadog/browser-logs';
import QuickRepliesCountrySelector from './QuickRepliesCountrySelector';
import { Country } from '../../../../../models/Country';
import { Button } from '@mui/material';
import { useAppContext } from '../../../../../AppContext';
import { QuickResponsesClient } from '@amazon-connect/quick-responses';

export interface QuickRepliesComponentProps {
  setShowReplies: (value: boolean) => void;
  quickReplies: connect.QuickResponse[];
}

interface QuickRepliesContainerProps {
  channel: 'chat' | 'email';
  selectedContact: string | null;
  QuickRepliesComponent: React.ComponentType<QuickRepliesComponentProps>;
}

const QuickRepliesContainer = ({
  channel,
  selectedContact,
  QuickRepliesComponent
}: QuickRepliesContainerProps): JSX.Element => {
  const { agent } = useAppContext();
  const [showReplies, setShowReplies] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const latestQuickRepliesFetchId = useRef(0);
  const quickResponsesClientRef = useRef<QuickResponsesClient | null>(null);
  const [quickReplies, setQuickReplies] = useState<connect.QuickResponse[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const agentName = agent?.getName();
  const isDisabled = !selectedContact;

  const toggleReplies = (): void => {
    setShowReplies(!showReplies);
  };

  useEffect(() => {
    const namePlaceholderRegex = /\[NAAM\]/g;
    const fetchId = ++latestQuickRepliesFetchId.current;

    const isLatestFetch = (): boolean => fetchId === latestQuickRepliesFetchId.current;

    if (isDisabled || !selectedCountryCode) {
      return;
    }

    const applyAgentNamePlaceholder = (reply: connect.QuickResponse): connect.QuickResponse => {
      if (reply.contents.plainText && agentName) {
        reply.contents.plainText.content = reply.contents.plainText.content.replace(namePlaceholderRegex, agentName);
      }

      return reply;
    };

    const fetchChatQuickReplies = async (): Promise<void> => {
      const response = await connect.QuickResponses.searchQuickResponses({
        query: selectedCountryCode
      });

      const channelFilteredReplies = response.results.filter(reply => {
        const channels = reply.channels?.map((ch: string) => ch.toLowerCase()) || [];
        // Chat still uses the legacy connect.QuickResponses API, which does not filter by channel
        // server-side. Exclude replies tagged for both chat and email so they don't leak into chat.
        if (channels.includes('chat') && channels.includes('email')) {
          return false;
        }
        return channels.includes('chat');
      });

      if (!isLatestFetch()) {
        return;
      }

      setQuickReplies(channelFilteredReplies.map(applyAgentNamePlaceholder));
    };

    const fetchEmailQuickReplies = async (): Promise<void> => {
      if (!quickResponsesClientRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        quickResponsesClientRef.current = new QuickResponsesClient(connect.core.getSDKClientConfig() as any);
      }

      const response = await quickResponsesClientRef.current.searchQuickResponses({
        channels: ['Email'],
        queries: selectedCountryCode
          ? [
              {
                name: 'name',
                values: [selectedCountryCode],
                operator: 'CONTAINS_AND_PREFIX'
              }
            ]
          : undefined
      });

      const emailReplies = response.results.map(result => {
        const plainTextContent = result.contents.plainText || result.contents.markdown || '';

        return {
          ...result,
          channels: ['Email'],
          contents: {
            plainText: {
              content: plainTextContent
            },
            markdown: {
              content: result.contents.markdown || ''
            }
          }
        } as unknown as connect.QuickResponse;
      });

      if (!isLatestFetch()) {
        return;
      }

      setQuickReplies(emailReplies.map(applyAgentNamePlaceholder));
    };

    const fetchQuickReplies = async (): Promise<void> => {
      if (channel === 'email') {
        await fetchEmailQuickReplies();
        return;
      }

      await fetchChatQuickReplies();
    };

    void fetchQuickReplies().catch((error: Error) => {
      datadogLogs.logger.error(`Unable to retrieve Quick Replies`, { quickReplies: true, channel }, error);
    });
  }, [selectedCountryCode, isDisabled, agentName, channel]);

  useClickOutside(containerRef, showReplies, () => setShowReplies(false));

  const handleCountrySelect = React.useCallback((country: Country): void => {
    setSelectedCountryCode(country.country);
  }, []);

  return (
    <Box ref={containerRef} sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ display: showReplies ? 'block' : 'none' }}>
        <QuickRepliesComponent setShowReplies={setShowReplies} quickReplies={quickReplies} />
      </Box>
      <QuickRepliesCountrySelector onCountrySelect={handleCountrySelect} disabled={isDisabled} channel={channel} />
      <Button
        data-testid={`${channel}-quick-replies-button`}
        sx={{
          height: '48px',
          padding: '0 16px',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          border: '1px solid #DDDDDD',
          background: showReplies ? '#F2F7FC' : 'inherit',
          borderColor: showReplies ? '#0090E3' : '#DDDDDD',
          marginRight: '8px'
        }}
        disabled={isDisabled}
        onClick={toggleReplies}
      >
        Quick Replies
      </Button>
    </Box>
  );
};

export default QuickRepliesContainer;
